import { z } from 'zod';

// ─── Enums ────────────────────────────────────────────────────────────────────

const scheduledQueryFrequencySchema = z.enum(['daily', 'weekly', 'monthly']);
const scheduledQueryWindowSchema = z.enum(['morning', 'afternoon', 'evening']);
const scheduledQueryDaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]);
const scheduledQueryOccurrenceSchema = z.enum([
  'first',
  'second',
  'third',
  'fourth',
  'last',
]);
const scheduledQueryStatusSchema = z.enum(['active', 'suspended']);
const scheduledQueryAnalysisModeSchema = z.enum(['Quick', 'Deep']);

// ─── Response schemas ─────────────────────────────────────────────────────────

const scheduledQueryGroupFilterSchema = z.object({
  policy: z.enum(['Allow', 'Block']),
  groups: z.array(z.string()),
});

const scheduledQueryRequestSchema = z.object({
  message: z.string(),
  analysis_mode: scheduledQueryAnalysisModeSchema,
  tools_config: z
    .object({
      group_filter: scheduledQueryGroupFilterSchema.optional(),
      parameters: z
        .object({
          web_search: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});

const scheduledQueryCadenceSchema = z.object({
  frequency: scheduledQueryFrequencySchema,
  time_window: scheduledQueryWindowSchema,
  day_of_week: scheduledQueryDaySchema.optional(),
  occurrence: scheduledQueryOccurrenceSchema.optional(),
  timezone: z.string(),
  skip_weekends: z.boolean().optional(),
});

const scheduledQuerySchema = z.object({
  id: z.string(),
  title: z.string(),
  request: scheduledQueryRequestSchema,
  cadence: scheduledQueryCadenceSchema,
  status: scheduledQueryStatusSchema,
  next_fire_at: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

const listScheduledQueriesResponseSchema = z.object({
  scheduled_queries: z.array(scheduledQuerySchema),
  total_count: z.number(),
});

const runNowResponseSchema = z.object({
  status: z.string(),
});

const scheduledQueryConversationTaskSchema = z.object({
  request_id: z.string(),
  start_time: z.string(),
  total_seconds: z.number().optional(),
  input: z.string().optional(),
  output: z.string().nullable().optional(),
  analysis_mode: scheduledQueryAnalysisModeSchema.optional(),
});

const scheduledQueryConversationSchema = z.object({
  id: z.string(),
  name: z.string(),
  access: z.string().optional(),
  favourite: z.boolean().optional(),
  updated_at: z.string().optional(),
  favourited_at: z.string().nullable().optional(),
  task: scheduledQueryConversationTaskSchema.optional(),
});

const listScheduledQueryConversationsResponseSchema = z.object({
  conversations: z.array(scheduledQueryConversationSchema),
});

// ─── Exported types ───────────────────────────────────────────────────────────

export type ScheduledQueryFrequency = z.infer<
  typeof scheduledQueryFrequencySchema
>;
export type ScheduledQueryWindow = z.infer<typeof scheduledQueryWindowSchema>;
export type ScheduledQueryDay = z.infer<typeof scheduledQueryDaySchema>;
export type ScheduledQueryOccurrence = z.infer<
  typeof scheduledQueryOccurrenceSchema
>;
export type ScheduledQueryStatus = z.infer<typeof scheduledQueryStatusSchema>;
export type ScheduledQueryAnalysisMode = z.infer<
  typeof scheduledQueryAnalysisModeSchema
>;
export type ScheduledQueryGroupFilter = z.infer<
  typeof scheduledQueryGroupFilterSchema
>;
export type ScheduledQueryRequest = z.infer<typeof scheduledQueryRequestSchema>;
export type ScheduledQueryCadence = z.infer<typeof scheduledQueryCadenceSchema>;
export type ScheduledQuery = z.infer<typeof scheduledQuerySchema>;
export type ListScheduledQueriesResponse = z.infer<
  typeof listScheduledQueriesResponseSchema
>;
export type RunNowResponse = z.infer<typeof runNowResponseSchema>;
export type ScheduledQueryConversationTask = z.infer<
  typeof scheduledQueryConversationTaskSchema
>;
export type ScheduledQueryConversation = z.infer<
  typeof scheduledQueryConversationSchema
>;
export type ListScheduledQueryConversationsResponse = z.infer<
  typeof listScheduledQueryConversationsResponseSchema
>;

// ─── Request body types ───────────────────────────────────────────────────────

export interface CreateScheduledQueryBody {
  title: string;
  request: ScheduledQueryRequest;
  cadence: ScheduledQueryCadence;
}

export type UpdateScheduledQueryBody = CreateScheduledQueryBody;

export interface ListScheduledQueriesParams {
  status?: ScheduledQueryStatus;
  page?: number;
  page_size?: number;
}

// ─── Route definitions ────────────────────────────────────────────────────────

const BASE = 'conversation-scheduler/v1/scheduled';
const CONVERSATIONS_BASE = 'conversation/v3/scheduled';

export const scheduledQueries = {
  list: {
    path: BASE,
    schema: listScheduledQueriesResponseSchema,
  },
  get: {
    path: (id: string) => `${BASE}/${id}`,
    schema: scheduledQuerySchema,
  },
  create: {
    path: BASE,
    schema: scheduledQuerySchema,
  },
  update: {
    path: (id: string) => `${BASE}/${id}`,
  },
  delete: {
    path: (id: string) => `${BASE}/${id}`,
  },
  runNow: {
    path: (id: string) => `${BASE}/${id}/run`,
    schema: runNowResponseSchema,
  },
  listConversations: {
    path: (id: string) => `${CONVERSATIONS_BASE}/${id}`,
    schema: listScheduledQueryConversationsResponseSchema,
  },
} as const;
